/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/api/api';
import { IApiActionResults, IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { AssessmentWithLocalState } from 'app/core/models/local-state-extensions';
import { AssessmentService } from 'app/core/services/assessment-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { Toaster } from 'app/shared/lib/toaster';
import { guid } from 'app/shared/lib/guid';
import * as Core from 'app/core/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

interface IExternalAssessmentAccess {
    cci: boolean;
}

@autoinject
export class AddAttachmentsCustomElement extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.boolean
    public showActions: boolean = false;

    @bindable.objectNull
    public config: IAssessmentsConfig | null = null;

    @bindable.arrayNull({ changeHandler: 'reload' })
    public existingAttachments: Core.AssessmentAttachment[] | null = null;

    @bindable
    protected mcgCopyright: string | undefined = '';

    protected assessments: AssessmentWithLocalState[] = [];
    protected selectedAssessments: AssessmentWithLocalState[] = [];

    protected mcgAssessments: AssessmentWithLocalState[] = [];
    protected selectedMcgAssessments: AssessmentWithLocalState[] = [];
    protected hasMcgAssessments: boolean = false;

    protected externalAssessmentAccess: IExternalAssessmentAccess | null = null;

    protected id: guid = guid();

    protected initialAssessmentCount: number = 0;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AssessmentService,
        private readonly mcgService: McgInformedService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!(this.existingAttachments && this.existingAttachments.length > 0)) {
            this.logger.warn('Array [existing-attachments] is null or empty - all assessments will be available for attachment');
        }

        this.service.configure(this.config);

        this.checkPermissions();

        this.reload();
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////

    private async reload(): Promise<void> {
        this.selectedAssessments.splice(0);
        this.selectedMcgAssessments.splice(0);

        this.loadAssessments();
    }

    private disableIncompleteAssessments(): void {
        if(this.existingAttachments && this.existingAttachments.length > 0) {
            const incompleteAssessmentIds = this.existingAttachments
                .filter(a => !a.isCompleted && a.isActive)
                .map(a => a.assessment && a.assessment.id);

            this.assessments.forEach(a => {
                a._state = {};

                // If assessment is attached but incomplete, add it to selectedAssessments (only completed assessments can be attached again)
                if(incompleteAssessmentIds.includes(a.id)) {
                    a._state.isAttached = true; // Will disable checkbox element to prevent user from de-selecting
                    this.selectedAssessments.push(a);
                }
            });

            this.mcgAssessments.forEach(a => {
                a._state = {};

                // If assessment is attached but incomplete, add it to selectedAssessments (only completed assessments can be attached again)
                if(incompleteAssessmentIds.includes(a.id)) {
                    a._state.isAttached = true; // Will disable checkbox element to prevent user from de-selecting
                    this.selectedMcgAssessments.push(a);
                }
            });

        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.externalAssessmentAccess = {
            cci: this.env.config.integrations.includes('cci') && this.accountContext.hasRouteAccess('cci.assessment')
        } as IExternalAssessmentAccess;
    }

    ////****************** IForm ****************************************************////

    public reset(): void {
        this.selectedAssessments.splice(0);
        this.selectedMcgAssessments.splice(0);
        this.disableIncompleteAssessments();
    }

    public hasChanges(): boolean {
        return this.selectedAssessments.some(a => { return !a._state || !a._state.isAttached }) ||
            this.selectedMcgAssessments.some(a => { return !a._state || !a._state.isAttached });
    }
    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(!this.hasChanges()) { return false; }

        return await this.addAssessments();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAssessments(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes: string[] = [
            'externalSystem'
        ];

        const fields: string[] = [
            'currentVersion.id',
            'description',
            'externalSystem.code'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAssessments(options, includes, null, fields);
            if(response.results && response.results.length > 0) {
                // Filter out assessments that are not activated (no current version)
                const activeAssessments: AssessmentWithLocalState[] = response.results.filter(a => !!a.currentVersion);

                // Filter out external system assessments
                this.assessments = activeAssessments.filter(a => !a.externalSystem);

                // Filter for MCG Assessments
                this.mcgAssessments = activeAssessments.filter(a => a.externalSystem?.code == 'CCI');
                this.hasMcgAssessments = this.mcgAssessments.length > 0;

            }
            this.disableIncompleteAssessments();
            this.initialAssessmentCount = this.selectedAssessments.length + this.selectedMcgAssessments.length;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async addAssessments(): Promise<boolean> {
        if(!(this.config && this.config.addTargetProperty)) {
            throw new Error("Configuration does not define [addTargetProperty]");
        }

        const promises = new Array<Promise<IApiActionResults>>();

        try {
            this.selectedAssessments
                // First filter out already attached assessments
                .filter(assessment => !(assessment._state && assessment._state.isAttached))
                // Map each Core.Assessment to a new Core.AssessmentAttachment for API insert
                .map(assessment => {
                    return {
                        assessment: {
                            id: assessment.id,
                            version: { id: assessment.currentVersion && assessment.currentVersion.id || null }
                        }
                    } as Core.AssessmentAttachment;
                })
                // Create a promise for each Core.AssessmentAttachment
                .forEach(attachment => {
                    if(this.config && this.config.addTargetProperty) {
                        this.config.addTargetProperty(attachment);
                    }
                    promises.push(this.service.insertAttachment(attachment));
                });


            this.state.beginSave();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            const success = responses.every(r => { return r.isSuccessful; });
            this.submittedChanges = this.selectedAssessments;

            let mcgSuccess = true;
            if(this.selectedMcgAssessments.length > 0) mcgSuccess = await this.addMcgAttachments();

            return success && mcgSuccess;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAttachments(attachmentIds: string[]): Promise<Core.AssessmentAttachment[]> {
        const promises = new Array<Promise<IApiQueryResults<Core.AssessmentAttachment>>>();
        const includes = ['assessment'];
        const fields = ['assessment.*']; //Load all for MCG Attachment JSON Content
        attachmentIds.forEach(id => { promises.push(this.service.loadAttachment(id, includes, [], fields)); });

        try {
            this.state.beginLoad();

            const responses = await Promise.all(promises);
            if(!responses.every(r => r.isSuccessful)) return [];

            const attachments: Core.AssessmentAttachment[] = [];
            responses.forEach(r => {
                attachments.push(...r.results);
            });

            return attachments;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error loading attachments');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    ////****************** MCG Service Calls ********************************************////
    @apiCall
    private async addMcgAttachments(): Promise<boolean> {
        const promises = new Array<Promise<IApiActionResults>>();

        try {
            this.selectedMcgAssessments
                // First filter out already attached assessments
                .filter(assessment => !(assessment._state && assessment._state.isAttached))
                // Map each Core.Assessment to a new Core.AssessmentAttachment for API insert
                .map(assessment => {
                    return {
                        assessment: {
                            id: assessment.id,
                            version: { id: assessment.currentVersion && assessment.currentVersion.id || null }
                        }
                    } as Core.AssessmentAttachment;
                })
                // Create a promise for each Core.AssessmentAttachment
                .forEach(attachment => {
                    if(this.config && this.config.addTargetProperty) {
                        this.config.addTargetProperty(attachment);
                    }
                    promises.push(this.service.insertAttachment(attachment));
                });


            this.state.beginSave();

            const responses = await Promise.all(promises);
            const success = responses.every(r => { return r.isSuccessful; });
            if(!success) return false;

            const attachmentIds: string[] = [];
            for(const r of responses) {
                if(!r.id) continue;
                attachmentIds.push(r.id);
            }

            // Need to load attachments to query for attachment assessment ids to pass to MCG
            const attachments: Core.AssessmentAttachment[] = await this.loadAttachments(attachmentIds);
            const mcgAssessments: Mcg.Assessment[] = await this.loadMcgAssessments(attachmentIds);
            const externalContentSuccess: boolean = await this.addMcgAttachmentExternalContent(attachments, mcgAssessments);

            return success && externalContentSuccess;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadMcgAssessments(attachmentIds: string[]): Promise<Mcg.Assessment[]> {
        const promises = new Array<Promise<IApiActionResults>>();

        attachmentIds.forEach(id => {
            if(!id) return;
            promises.push(this.mcgService.getAssessmentContent(id));
        });

        try {
            this.state.beginLoad();

            const responses = await Promise.all(promises);
            if(!responses.every(r => r.isSuccessful)) return [];

            const assessments: Mcg.Assessment[] = [];
            responses.forEach(r => {
                assessments.push(r.result);
            });

            return assessments;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addMcgAttachmentExternalContent(attachments: Core.AssessmentAttachment[], mcgAssessments: Mcg.Assessment[]): Promise<boolean> {
        const promises = new Array<Promise<IApiActionResults>>();

        try {
            attachments.forEach(a => {
                const assessment: Mcg.Assessment | undefined = mcgAssessments.find(mcg => mcg.hsim === a.assessment?.externalAssessmentIdentifier);
                if(!assessment) return;

                const externalContent: Core.AssessmentAttachmentExternalContent = {
                    assessmentAttachment: { id: a.id },
                    externalContentType: { code: 'AssessmentJson' },
                    content: JSON.stringify(assessment),
                    mediaType: { code: 'data.json' }
                };

                promises.push(this.service.insertAttachmentExternalContent(externalContent));
            });

            this.state.beginSave();

            const responses = await Promise.all(promises);
            if(!responses.every(r => r.isSuccessful)) return false;

            return true;
        }
        catch(error) {
            this.toaster.standard.added('am:assessment.$', false, promises.length);
            this.logger.error('Error adding assessments');
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
